import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { NavItem } from '@tmapy/config';

import type { AppState } from '@tmapy/redux';
import { useLocation } from '@tmapy/router';
import type { RouteStateTWC } from '@tmapy/types';

export const getParentRouteInMenu = (
  route: RouteStateTWC | null,
  routes: RouteStateTWC[],
  menu: NavItem[],
): string | null => {
  const visitedRouteIds = new Set<string>();
  let currentRoute = route;

  while (currentRoute) {
    const currentRouteId = currentRoute.id;

    if (visitedRouteIds.has(currentRouteId)) {
      throw new Error(`Cyclic parentRouteId detected: "${currentRouteId}"`);
    }
    visitedRouteIds.add(currentRouteId);

    const isCurrentRouteInMenu = menu.some((menuItem) => menuItem.to === currentRouteId);
    if (isCurrentRouteInMenu) {
      return currentRouteId;
    }

    const parentRouteId: string | undefined = currentRoute?.context?.parentRouteId;
    if (!parentRouteId) {
      return null;
    }

    currentRoute = routes.find((routeItem) => routeItem.id === parentRouteId) ?? null;
  }

  return null;
};

export const useParentRouteInMenu = (): string | null => {
  const location = useLocation();
  const route = location.route as RouteStateTWC | null;

  const { routes, menu } = useSelector(({ app }: AppState) => ({
    routes: app?.routes ?? [],
    menu: app?.menu ?? [],
  }));

  return useMemo(() => getParentRouteInMenu(route, routes, menu), [route?.id, routes, menu]);
};
